import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ETAPPIO_COMPANY,
  ETAPPIO_COMPANY_VISITING_ADDRESS
} from '../constants/company';

type CompanyLegalDetailsProps = {
  variant?: 'card' | 'compact';
  theme?: 'light' | 'dark';
  headingLevel?: 'h2' | 'h3';
  showContactPageLink?: boolean;
  className?: string;
};

/**
 * Etappio's company and contact details as users see them. Per the address
 * policy only the visiting address is shown here; the registered office
 * (säte) belongs in legal texts such as the terms, not in contact details.
 */
export function CompanyLegalDetails({
  variant = 'card',
  theme = 'light',
  headingLevel = 'h2',
  showContactPageLink = true,
  className = ''
}: CompanyLegalDetailsProps) {
  const { t } = useTranslation();
  const Heading = headingLevel;
  const mutedClass = theme === 'dark' ? 'text-white-50' : 'text-muted';
  const linkClass = `contact-touch-link d-inline-flex align-items-center ${theme === 'dark' ? 'text-white' : ''}`;

  if (variant === 'compact') {
    return (
      <address
        className={`company-legal-address mb-0 small ${theme === 'dark' ? 'text-white-50' : 'text-muted'} ${className}`.trim()}
        data-testid="company-legal-details"
      >
        <strong className={theme === 'dark' ? 'text-white' : 'text-dark'}>
          {ETAPPIO_COMPANY.legalName}
        </strong>
        {' · '}
        {t('companyDetails.tradeName')}: {ETAPPIO_COMPANY.tradeName}
        {' · '}
        {t('companyDetails.organizationNumberShort')} {ETAPPIO_COMPANY.organizationNumber}
        <br />
        {t('companyDetails.visitingAddress')}: {ETAPPIO_COMPANY_VISITING_ADDRESS}
        {' · '}
        <a className={linkClass} href={`mailto:${ETAPPIO_COMPANY.email}`}>
          {ETAPPIO_COMPANY.email}
        </a>
        {showContactPageLink && (
          <>
            {' · '}
            <Link className={linkClass} to="/kontakt">
              {t('companyDetails.contactPage')}
            </Link>
          </>
        )}
      </address>
    );
  }

  return (
    <section
      className={`${theme === 'dark' ? 'bg-white bg-opacity-10 text-white' : 'bg-white border'} rounded-4 p-4 h-100 ${className}`.trim()}
      data-testid="company-legal-details"
    >
      <Heading className="fw-bold mb-3">{t('companyDetails.title')}</Heading>
      <dl className="mb-0">
        <dt className={`small ${mutedClass}`}>{t('companyDetails.legalName')}</dt>
        <dd className="fw-semibold mb-3">{ETAPPIO_COMPANY.legalName}</dd>

        <dt className={`small ${mutedClass}`}>{t('companyDetails.tradeName')}</dt>
        <dd className="fw-semibold mb-3">{ETAPPIO_COMPANY.tradeName}</dd>

        <dt className={`small ${mutedClass}`}>{t('companyDetails.organizationNumber')}</dt>
        <dd className="fw-semibold mb-3">{ETAPPIO_COMPANY.organizationNumber}</dd>

        <dt className={`small ${mutedClass}`}>{t('companyDetails.visitingAddress')}</dt>
        <dd className="mb-3">
          <address className="company-legal-address mb-0">
            {ETAPPIO_COMPANY.streetAddress}<br />
            {ETAPPIO_COMPANY.postalCode} {ETAPPIO_COMPANY.locality}
          </address>
        </dd>

        <dt className={`small ${mutedClass}`}>{t('companyDetails.email')}</dt>
        <dd className="mb-0">
          <a className={linkClass} href={`mailto:${ETAPPIO_COMPANY.email}`}>
            {ETAPPIO_COMPANY.email}
          </a>
        </dd>
      </dl>

      {showContactPageLink && (
        <Link className={`d-inline-block mt-3 ${linkClass}`.trim()} to="/kontakt">
          {t('companyDetails.contactPage')}
        </Link>
      )}
    </section>
  );
}
